import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  Mapping,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { CreateRelationshipMappingOptions } from 'src/containers/ProjectDiagram/utils/mapping/createRelationshipMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import {
  RelationshipSuggestion,
  Suggestion,
  SuggestionType,
} from 'src/containers/ProjectMapping/types';
import { projectMappingRelationshipsSelector } from 'src/selectors/projectMappingRelationships';
import { projectMappingsSelector } from 'src/selectors/projectMappings';
import { projectRelationshipMappingsSelector } from 'src/selectors/projectRelationshipMappings';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

// hooks

const getSuggestionKey = (
  relationshipIri: string,
  classIri: string,
  opposingClassIri: string,
  direction: RelationshipDirection
) => `${relationshipIri} ${classIri} ${opposingClassIri} ${direction}`;

type RelationshipSuggestionsByKey = Record<string, RelationshipSuggestion>;

const organizeSuggestions = (
  suggestions: Suggestion[]
): RelationshipSuggestionsByKey => {
  const relationshipSuggestionsByKey: RelationshipSuggestionsByKey = {};
  Object.values(suggestions).forEach((suggestion) => {
    if (
      suggestion.type !== SuggestionType.RELATIONSHIP ||
      !suggestion.propertyType
    ) {
      return;
    }

    relationshipSuggestionsByKey[
      getSuggestionKey(
        suggestion.iri,
        suggestion.classIri,
        suggestion.propertyType,
        suggestion.direction
      )
    ] = suggestion;
  });

  return relationshipSuggestionsByKey;
};

const getMappingKey = (
  relationshipIri: string,
  fromIri: string,
  toIri: string
) => `${relationshipIri} ${fromIri} ${toIri}`;

const organizeClassMappings = (mappings: Mapping[], resourceId: string) => {
  const classMappingsByIri: Record<string, Mapping> = {};
  mappings.forEach((mapping) => {
    if (getResourceValue(mapping.from) !== resourceId || !mapping.to) {
      return;
    }

    const classIri = getModelConceptClassValue(mapping.to);
    classMappingsByIri[classIri] = mapping;
  });

  return classMappingsByIri;
};

const organizeRelationshipMappings = (
  relationshipMappings: RelationshipMapping[],
  mapping: Mapping
) => {
  const existingRelationshipMappings: Record<string, RelationshipMapping> = {};
  Object.values(relationshipMappings).forEach((relationshipMapping) => {
    if (!mapping.to) {
      return;
    }

    if (
      relationshipMapping.from &&
      relationshipMapping.from === mapping.key &&
      relationshipMapping.toClassIri
    ) {
      existingRelationshipMappings[
        getMappingKey(
          relationshipMapping.relationshipIri,
          getModelConceptClassValue(mapping.to),
          relationshipMapping.toClassIri
        )
      ] = relationshipMapping;
    }

    if (
      relationshipMapping.to &&
      relationshipMapping.to === mapping.key &&
      relationshipMapping.fromClassIri
    ) {
      existingRelationshipMappings[
        getMappingKey(
          relationshipMapping.relationshipIri,
          relationshipMapping.fromClassIri,
          getModelConceptClassValue(mapping.to)
        )
      ] = relationshipMapping;
    }
  });

  return existingRelationshipMappings;
};

export const useRelationshipMappingsFromSuggestions = (
  resourceId: string | undefined
) => {
  const mapping = useMappingFromRoute();
  const diagram = useDiagram();
  const { projectId } = useRouterParams();

  const relationshipsByIri = useReduxSelector(
    projectMappingRelationshipsSelector(projectId, mapping?.value)
  );
  const allRelationshipMappings = useReduxSelector(
    // projectRelationshipMappingsSelector is not memoized (takes an object
    // filter arg), so keep isEqual until it is memoized separately.
    projectRelationshipMappingsSelector(projectId),
    isEqual
  );
  const allClassMappings = useReduxSelector(projectMappingsSelector(projectId));

  return React.useCallback(
    (suggestions: Suggestion[]) => {
      if (!diagram || !resourceId || !mapping?.to) {
        return [];
      }

      const classIri = getModelConceptClassValue(mapping.to);
      const classMappingsByIri = organizeClassMappings(
        allClassMappings,
        resourceId
      );
      const relationshipSuggestionsByKey = organizeSuggestions(suggestions);
      const existingRelationshipMappings = organizeRelationshipMappings(
        allRelationshipMappings,
        mapping
      );

      const relationshipMappingsToCreate = Object.values(
        relationshipsByIri
      ).reduce<CreateRelationshipMappingOptions[]>((acc, relationship) => {
        if (!relationship.domains.size || !relationship.ranges.size) {
          return acc;
        }

        if (
          relationship.domains.has(classIri) ||
          Array.from(relationship.sourceClassIris).some((sourceClassIri) =>
            relationship.domains.has(sourceClassIri)
          )
        ) {
          Array.from(relationship.ranges).forEach((range) => {
            // skip circular relationships
            if (classIri === range) {
              return;
            }

            const relationshipSuggestion =
              relationshipSuggestionsByKey[
                getSuggestionKey(
                  relationship.iri,
                  classIri,
                  range,
                  RelationshipDirection.OUTGOING
                )
              ];

            // skip existing relationship mappings
            if (
              relationshipSuggestion &&
              !existingRelationshipMappings[
                getMappingKey(relationship.iri, classIri, range)
              ]
            ) {
              const opposingMapping = classMappingsByIri[range];
              const newIdentifier = {
                baseIri: opposingMapping?.primaryIdentifier.baseIri || '',
                parts: [
                  ...(opposingMapping?.primaryIdentifier.parts ||
                    relationshipSuggestion.fields ||
                    []),
                ],
              };
              acc.push({
                from: mapping.key,
                relationshipIri: relationship.iri,
                toClassIri: range,
                toPrimaryIdentifier: newIdentifier,
              });
            }
          });
        }

        if (
          relationship.ranges.has(classIri) ||
          Array.from(relationship.sourceClassIris).some((sourceClassIri) =>
            relationship.ranges.has(sourceClassIri)
          )
        ) {
          Array.from(relationship.domains).forEach((domain) => {
            // skip circular relationships
            if (classIri === domain) {
              return;
            }

            const relationshipSuggestion =
              relationshipSuggestionsByKey[
                getSuggestionKey(
                  relationship.iri,
                  classIri,
                  domain,
                  RelationshipDirection.INCOMING
                )
              ];

            // skip existing relationship mappings
            if (
              relationshipSuggestion &&
              !existingRelationshipMappings[
                getMappingKey(relationship.iri, domain, classIri)
              ]
            ) {
              const opposingMapping = classMappingsByIri[domain];
              const newIdentifier = {
                baseIri: opposingMapping?.primaryIdentifier.baseIri || '',
                parts: [
                  ...(opposingMapping?.primaryIdentifier.parts ||
                    relationshipSuggestion.fields ||
                    []),
                ],
              };
              acc.push({
                fromClassIri: domain,
                fromPrimaryIdentifier: newIdentifier,
                relationshipIri: relationship.iri,
                to: mapping.key,
              });
            }
          });
        }

        return acc;
      }, []);

      return relationshipMappingsToCreate;
    },
    [
      allClassMappings,
      allRelationshipMappings,
      diagram,
      mapping,
      relationshipsByIri,
      resourceId,
    ]
  );
};
